import { queryOptions, useQuery } from '@tanstack/react-query'
import { Component, useEffect, useRef, useState, type ReactNode } from 'react'
import {
  deviceQuickDetailsQuery,
  deviceSoftseeQuickDetailsQuery,
  performanceTrendQuery,
  systemAlarmsListQuery,
  type PerformanceTrend,
  type ProactiviteeLivePayload,
  type SystemAlarmOpen,
} from '../api/devices'
import { apiGet } from '../api/client'
import { ticketDevicesQuery, type TicketDeviceBox } from '../api/easeedesk'
import { acquirePortalLink, type PortalStatus } from '../api/portalSocket'
import { realtimeConfigQuery } from '../api/realtime'
import { deviceHref } from '../devices/DeviceWorkspace'
import { fromNow, stamp } from '../inventoree/format'
import { durationString } from './format'
import { Section } from './TicketSummaryBlocks'
import { NEW_TAB } from '../ui/NewTab'

/**
 * Masina tichetului, pe scurt - coloana din dreapta a rezumatului, sub masini.
 *
 * Perechea veche: `summary/device_widgets.php`, incarcata de
 * `Easeedesk::ticketDevicesPanel()` dupa tichet, cu cele patru widgeturi ale
 * PRIMULUI aparat (`Health::slideSummary`, `Health::liveScreenShot`,
 * `Hardware::systemStatus`, `Software::operatingSystem`). In v4 nu fusesera
 * aduse, si pe tichetele cu aparat dreapta parea goala.
 *
 * Cinci blocuri, toate scurte:
 *
 *   - Machine health: procesor / memorie / disc acum, cu un grafic mic pe
 *     ultimele sase ore (SVG scris de mana, `Spark` mai jos - fara biblioteca);
 *   - Configuration: model, procesor, RAM, disc, sistem, de cand e pornita;
 *   - Live screenshot: NUMAI la clic. Portalul o cerea singur, din minut in
 *     minut, cat statea omul pe tichet - e grea si intra pe ecranul cuiva;
 *   - Open alarms: alarmele Proactivitee deschise;
 *   - Changes (7 days): hardware si software, cele mai noi sus.
 *
 * NICIUN CAPAT NOU. Fiecare bloc cere capatul pe care il foloseste deja fisa
 * aparatului, deci poarta dreptul ecranului de acolo si gardul de firma de
 * acolo: cine n-are dreptul "System Alarms" primeste 403, si blocul pur si
 * simplu nu se deseneaza. Masina vine din `ticketDevicesQuery`, adica din
 * dreptul tichetului - aceeasi cerere ca "User Devices", deci nicio cerere in
 * plus pentru lista.
 *
 * Fiecare bloc isi are cererea lui: o eroare intr-unul nu le atinge pe
 * celelalte, iar un bloc fara nimic de spus nu se deseneaza (nici in asteptare -
 * un titlu care apare si dispare e mai rau decat unul care apare o data).
 */

/** Cate randuri arata blocurile de lista inainte de "+N more". */
const SHOWN = 5

/** Ferestra graficului mic. */
const SPARK_HOURS = 6

/** Cat se asteapta captura dupa clic, inainte sa se spuna ca n-a venit. */
export const SCREENSHOT_WAIT_MS = 30_000

export function TicketDevicePanel({ ticket }: { ticket: number }) {
  const { data } = useQuery(ticketDevicesQuery(ticket))
  const listed: unknown = data?.affected?.devices
  const devices = (Array.isArray(listed) ? (listed as TicketDeviceBox[]) : []).filter((d) => typeof d?.id === 'number' && d.id > 0)
  const [chosen, setChosen] = useState<number | null>(null)

  if (devices.length === 0) return null

  const device = devices.find((d) => d.id === chosen) ?? devices[0]

  return (
    <div className="tk-device-panel" data-device={device.id}>
      {devices.length > 1 && <Picker devices={devices} value={device.id} onPick={setChosen} />}
      {/* `key`: la schimbarea masinii, blocurile pornesc de la zero (captura
          ceruta pentru alta masina nu ramane pe ecran). */}
      <MachineBlocks key={device.id} device={device} />
    </div>
  )
}

function Picker({ devices, value, onPick }: { devices: TicketDeviceBox[]; value: number; onPick(id: number): void }) {
  return (
    <div className="tk-device-picker" style={{ marginBottom: 8 }}>
      <label style={{ fontWeight: 'normal', marginRight: 6 }} htmlFor="tk-device-pick">
        Machine
      </label>
      <select id="tk-device-pick" className="form-control input-sm" style={{ display: 'inline-block', width: 'auto' }} value={value} onChange={(ev) => onPick(Number(ev.target.value))}>
        {devices.map((d) => (
          <option key={d.id} value={d.id}>
            {d.name}
          </option>
        ))}
      </select>
    </div>
  )
}

function MachineBlocks({ device }: { device: TicketDeviceBox }) {
  return (
    <>
      <Quiet>
        <MachineHealth id={device.id} />
      </Quiet>
      <Quiet>
        <MachineConfig id={device.id} />
      </Quiet>
      {/* Live screenshot - scos momentan (14 octombrie 2026, Roxana): o captura
          a ecranului omului cere, la banca, acordul lui si politica lor. Blocul
          ramane scris mai jos; se pune inapoi punand SHOW_LIVE_SCREENSHOT = true. */}
      {SHOW_LIVE_SCREENSHOT ? (
        <Quiet>
          <LiveScreenshot id={device.id} />
        </Quiet>
      ) : null}
      <Quiet>
        <OpenAlarms id={device.id} />
      </Quiet>
      <Quiet>
        <RecentChanges id={device.id} />
      </Quiet>
    </>
  )
}

/**
 * Un bloc care se strica la desenare tace, in loc sa ia cu el tot rezumatul.
 * Cererile picate tac oricum (fiecare bloc se uita numai la `data`); asta e
 * plasa pentru un raspuns de alta forma decat cea asteptata.
 */
class Quiet extends Component<{ children: ReactNode }, { broken: boolean }> {
  state = { broken: false }

  static getDerivedStateFromError() {
    return { broken: true }
  }

  componentDidCatch() {
    /* Nimic de facut: blocul nu se mai deseneaza. */
  }

  render() {
    return this.state.broken ? null : this.props.children
  }
}

/* ------------------------------------------------------------------------- */
/*  CERERILE                                                                  */
/* ------------------------------------------------------------------------- */

/**
 * `/system-performance/live` o data. `systemPerformanceLiveQuery` din fisa
 * intreaba din secunda in secunda - potrivit pe fila aia, nu intr-un rezumat.
 */
export function machineNowQuery(id: number) {
  return queryOptions({
    queryKey: ['ticket-device-panel', id, 'now'],
    queryFn: () => apiGet<ProactiviteeLivePayload>(`/devices/${id}/system-performance/live`),
    refetchOnWindowFocus: false,
  })
}

/* ------------------------------------------------------------------------- */
/*  MACHINE HEALTH                                                            */
/* ------------------------------------------------------------------------- */

const METRICS = [
  { key: 'cpu_util', label: 'CPU', color: '#C43626' },
  { key: 'ram_used_percent', label: 'Memory', color: '#8e7cc3' },
  { key: 'disk_util', label: 'Disk (busy)', color: '#36a3d9' },
] as const

function MachineHealth({ id }: { id: number }) {
  const now = useQuery(machineNowQuery(id))
  const cpu = useQuery(performanceTrendQuery(id, '24h', 'cpu_util'))
  const ram = useQuery(performanceTrendQuery(id, '24h', 'ram_used_percent'))
  const disk = useQuery(performanceTrendQuery(id, '24h', 'disk_util'))
  const trends = [cpu.data, ram.data, disk.data]

  const live = now.data?.has === true ? now.data : null
  const rows = METRICS.map((m, i) => {
    const points = recent(trends[i])
    const current = live ? Number(live[m.key] ?? NaN) : points.length ? points[points.length - 1] : NaN
    return { ...m, points, current }
  }).filter((r) => Number.isFinite(r.current) || r.points.length > 1)

  if (rows.length === 0) return null

  return (
    <Section title="Machine health">
      <table className="details-table tk-health" width="100%">
        <tbody>
          {rows.map((r) => (
            <tr key={r.key} data-metric={r.key}>
              <td className="title" style={{ width: '28%' }}>
                {r.label}
              </td>
              <td style={{ width: '18%' }}>
                <strong className={tone(r.current)}>{Number.isFinite(r.current) ? `${Math.round(r.current)}%` : '-'}</strong>
              </td>
              <td>{r.points.length > 1 ? <Spark values={r.points} color={r.color} label={`${r.label}, last ${SPARK_HOURS} hours`} /> : null}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {live?.age_seconds != null && (
        <div className="font-10" style={{ color: '#999', marginBottom: 10 }}>
          Measured {durationString(live.age_seconds)} ago &middot; chart: last {SPARK_HOURS} hours
        </div>
      )}
    </Section>
  )
}

function tone(value: number): string {
  if (!Number.isFinite(value)) return ''
  if (value >= 90) return 'color-danger'
  if (value >= 75) return 'color-warning'
  return 'color-green'
}

/**
 * Ultimele SPARK_HOURS ore dintr-un `trend` de 24h, adunate in cel mult 60 de
 * puncte. Punctele n-au data intreaga (`H:i`), dar treapta spune cat de des
 * sunt: pe minut sau la cinci minute; o treapta mai rara se ia toata.
 */
export function recent(trend: PerformanceTrend | undefined): number[] {
  if (!trend || !Array.isArray(trend.points) || trend.points.length === 0) return []
  const perHour = trend.tier === 'minute' ? 60 : trend.tier === 'five' ? 12 : 0
  const tail = perHour > 0 ? trend.points.slice(-SPARK_HOURS * perHour) : trend.points
  const values = tail.map((p) => Number(p.avg)).filter((v) => Number.isFinite(v))
  return squeeze(values, 60)
}

/** Medii pe bucati egale, ca graficul sa aiba cel mult `max` puncte. */
export function squeeze(values: number[], max: number): number[] {
  if (values.length <= max) return values
  const out: number[] = []
  const size = values.length / max
  for (let i = 0; i < max; i++) {
    const part = values.slice(Math.floor(i * size), Math.floor((i + 1) * size))
    out.push(part.reduce((a, b) => a + b, 0) / Math.max(1, part.length))
  }
  return out
}

/**
 * Graficul mic: o linie SVG pe 0-100%, scrisa de mana. Fara biblioteca de
 * grafice - pentru o linie de 60 de puncte nu e nevoie de una.
 */
export function Spark({ values, color, label }: { values: number[]; color: string; label: string }) {
  const width = 120
  const height = 24
  const step = values.length > 1 ? width / (values.length - 1) : 0
  const y = (v: number) => (height - 1 - (Math.max(0, Math.min(100, v)) / 100) * (height - 2)).toFixed(1)
  const points = values.map((v, i) => `${(i * step).toFixed(1)},${y(v)}`).join(' ')

  return (
    <svg className="tk-spark" viewBox={`0 0 ${width} ${height}`} width="100%" height={height} preserveAspectRatio="none" role="img" aria-label={label}>
      <line x1="0" x2={width} y1={height - 0.5} y2={height - 0.5} stroke="#e5e5e5" strokeWidth="1" />
      <polyline points={points} fill="none" stroke={color} strokeWidth="1.5" vectorEffect="non-scaling-stroke" />
    </svg>
  )
}

/* ------------------------------------------------------------------------- */
/*  CONFIGURATION                                                             */
/* ------------------------------------------------------------------------- */

function MachineConfig({ id }: { id: number }) {
  const hw = useQuery(deviceQuickDetailsQuery(id))
  const sw = useQuery(deviceSoftseeQuickDetailsQuery(id))
  const now = useQuery(machineNowQuery(id))

  const d = hw.data
  const model = [d?.device?.brand, d?.device?.model].filter((x) => x).join(' ')
  const os = sw.data?.summary?.operating_system
  const uptime = now.data?.has === true ? now.data.uptime_seconds : null

  const rows: [string, unknown][] = [
    ['Model', model],
    ['Processor', d?.cpu],
    ['RAM', d?.ram],
    ['Disk', d?.storage],
    ['Operating system', os],
    ['Up for', typeof uptime === 'number' ? durationString(uptime) : ''],
  ]
  const shown = rows.filter((r): r is [string, string] => typeof r[1] === 'string' && r[1].trim() !== '')

  if (shown.length === 0) return null

  return (
    <Section title="Configuration">
      <table className="details-table tk-config" width="100%">
        <tbody>
          {shown.map(([k, v]) => (
            <tr key={k}>
              <td className="title">{k}</td>
              <td>{v}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Section>
  )
}

/* ------------------------------------------------------------------------- */
/*  LIVE SCREENSHOT                                                           */
/* ------------------------------------------------------------------------- */

/**
 * Captura de ecran, NUMAI la cerere.
 *
 * Acelasi drum ca in portal (`ioSocket.requestScreenshot` din `general.js`):
 * evenimentul `screenshot` pe socketul portalului, cu raspunsul
 * `{content: <png base64>}`. Legatura de socket se ia abia la clic si se da
 * inapoi cand blocul se inchide - pana la clic nu se cere nimic, nici
 * adresa serverului.
 */
/** Scos momentan din panou - vezi locul unde se deseneaza. */
const SHOW_LIVE_SCREENSHOT = false

function LiveScreenshot({ id }: { id: number }) {
  const [round, setRound] = useState(0)

  return (
    <Section title="Live screenshot">
      {round === 0 ? (
        <div>
          <button type="button" className="btn btn-xs btn-default tk-take-screenshot" onClick={() => setRound(1)}>
            <i className="fas fa-camera" aria-hidden="true"></i> Take screenshot
          </button>
          <span className="font-10" style={{ color: '#999', marginLeft: 8 }}>
            Shows the user&apos;s screen as it is right now.
          </span>
        </div>
      ) : (
        <Screenshot key={round} id={id} again={() => setRound(round + 1)} />
      )}
    </Section>
  )
}

type ShotState = { kind: 'waiting' } | { kind: 'image'; src: string; at: Date } | { kind: 'silent' } | { kind: 'off'; status: PortalStatus }

function Screenshot({ id, again }: { id: number; again(): void }) {
  const { data: config, isError } = useQuery(realtimeConfigQuery)
  const [state, setState] = useState<ShotState>({ kind: 'waiting' })
  const asked = useRef(false)

  useEffect(() => {
    if (config === undefined) return
    const link = acquirePortalLink(config)
    asked.current = false

    const off = link.on('screenshot', id, (payload) => {
      const content = typeof payload === 'string' ? payload : (payload as { content?: unknown } | null)?.content
      if (typeof content === 'string' && content !== '') {
        setState({ kind: 'image', src: `data:image/png;base64,${content}`, at: new Date() })
      }
    })
    const ask = (status: PortalStatus) => {
      if (status === 'unconfigured' || status === 'error') {
        setState({ kind: 'off', status })
        return
      }
      if (status === 'ready' && !asked.current) {
        asked.current = true
        link.ask('screenshot', id)
      }
    }
    const unwatch = link.watch(ask)
    ask(link.status)

    const timer = setTimeout(() => setState((s) => (s.kind === 'waiting' ? { kind: 'silent' } : s)), SCREENSHOT_WAIT_MS)

    return () => {
      clearTimeout(timer)
      unwatch()
      off()
      link.release()
    }
  }, [config, id])

  const retry = (
    <button type="button" className="btn btn-xs btn-default tk-take-screenshot" onClick={again} style={{ marginTop: 6 }}>
      <i className="fas fa-redo" aria-hidden="true"></i> Take again
    </button>
  )

  if (isError || state.kind === 'off') {
    return <div className="alert alert-warning tk-shot-off">The live connection is not available, so no screenshot can be taken now.</div>
  }
  if (state.kind === 'silent') {
    return (
      <div>
        <div className="alert alert-info tk-shot-silent">The machine did not send a screenshot. It may be off or not connected.</div>
        {retry}
      </div>
    )
  }
  if (state.kind === 'waiting') {
    return (
      <div className="tk-shot-waiting" style={{ color: '#999' }}>
        <i className="fas fa-spinner fa-spin" aria-hidden="true"></i> Asking the machine for a screenshot...
      </div>
    )
  }

  return (
    <div className="tk-shot">
      <a href={state.src} target="_blank" rel="noreferrer">
        <img src={state.src} alt="Live screenshot" style={{ width: '100%', maxHeight: 280, objectFit: 'contain', border: '1px solid #ddd' }} />
      </a>
      <div className="font-10" style={{ color: '#999' }}>
        Taken at {state.at.toLocaleTimeString()}
      </div>
      {retry}
    </div>
  )
}

/* ------------------------------------------------------------------------- */
/*  OPEN ALARMS                                                               */
/* ------------------------------------------------------------------------- */

const SEVERITY: Record<number, { label: string; tone: string }> = {
  3: { label: 'Critical', tone: 'label-danger' },
  2: { label: 'Major', tone: 'label-warning' },
  1: { label: 'Minor', tone: 'label-success' },
}

function OpenAlarms({ id }: { id: number }) {
  const { data } = useQuery(systemAlarmsListQuery(id, 'all'))
  const alarms: SystemAlarmOpen[] = Array.isArray(data?.alarms) ? data.alarms : []

  if (alarms.length === 0) return null

  /* Serverul le da pe gravitate; la aceeasi gravitate, cea mai noua intai. */
  const sorted = [...alarms].sort((a, b) => b.severity - a.severity || String(b.created).localeCompare(String(a.created)))
  const href = deviceHref(id, 'proactivitee', 'proactivitee/deviceAlarms/', '?home=live')

  return (
    <Section title="Open alarms">
      <table className="details-table tk-alarms" width="100%">
        <tbody>
          {sorted.slice(0, SHOWN).map((a) => {
            const sev = SEVERITY[a.severity] ?? { label: 'Info', tone: 'label-primary' }
            const since = stamp(a.created)
            const what = [a.hardware_identifier, a.value_string].filter((x) => x && String(x).trim() !== '').join(' - ')
            return (
              <tr key={a.ID}>
                <td style={{ width: 70 }}>
                  <span className={`label ${sev.tone}`}>{sev.label}</span>
                </td>
                <td>
                  {a.type_name}
                  {what !== '' && <span style={{ color: '#999' }}> ({what})</span>}
                </td>
                <td className="align-right" style={{ whiteSpace: 'nowrap', color: '#999' }} title={a.created}>
                  {since ? fromNow(since) : ''}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
      <a href={href} className="font-11" style={{ display: 'inline-block', marginBottom: 10 }} {...NEW_TAB}>
        {sorted.length > SHOWN ? `+${sorted.length - SHOWN} more` : 'All alarms'}
      </a>
    </Section>
  )
}

/* ------------------------------------------------------------------------- */
/*  CHANGES (7 DAYS)                                                          */
/* ------------------------------------------------------------------------- */

interface Change {
  when: Date
  date: string
  kind: 'Hardware' | 'Software'
  message: string
}

/** `H:i d/m/Y` - cum scriu data `quick-details` si `softsee-quick-details`. */
export function parseEventDate(value: string): Date | null {
  const m = /^(\d{1,2}):(\d{2}) (\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(value.trim())
  if (!m) return stamp(value)
  const when = new Date(Number(m[5]), Number(m[4]) - 1, Number(m[3]), Number(m[1]), Number(m[2]))
  return isNaN(when.getTime()) ? null : when
}

/**
 * Hardware (it_events, `inventoree`/`hardware`) si software (it_events,
 * `softsee`: instalari, dezinstalari, actualizari), din cele doua capete pe care
 * fila Device a tichetului le cere deja. Fiecare da ultimele zece; de aici se
 * pastreaza cele din ultimele sapte zile. "+N more" numara din ce s-a primit,
 * deci pe o masina foarte agitata pot fi mai multe - fisa le are pe toate.
 */
function RecentChanges({ id, now = new Date() }: { id: number; now?: Date }) {
  const hw = useQuery(deviceQuickDetailsQuery(id))
  const sw = useQuery(deviceSoftseeQuickDetailsQuery(id))
  const since = now.getTime() - 7 * 24 * 3600 * 1000

  const all: Change[] = []
  const add = (kind: Change['kind'], events: { date: string; message: string }[] | undefined) => {
    for (const e of Array.isArray(events) ? events : []) {
      if (typeof e?.date !== 'string' || typeof e.message !== 'string') continue
      const when = parseEventDate(e.date)
      if (when && when.getTime() >= since && e.message.trim() !== '') all.push({ when, date: e.date, kind, message: e.message })
    }
  }
  add('Hardware', hw.data?.events)
  add('Software', sw.data?.events)

  if (all.length === 0) return null

  all.sort((a, b) => b.when.getTime() - a.when.getTime())
  const hidden = all.slice(SHOWN)
  const softwareHidden = hidden.filter((c) => c.kind === 'Software').length
  const href =
    hidden.length > 0 && softwareHidden * 2 > hidden.length
      ? deviceHref(id, 'softsee', 'softsee/deviceEvents/')
      : deviceHref(id, 'inventoree', 'inventoree/deviceEvents/')

  return (
    <Section title="Changes (last 7 days)">
      <table className="details-table tk-changes" width="100%">
        <tbody>
          {all.slice(0, SHOWN).map((c, i) => (
            <tr key={i}>
              <td style={{ width: 70 }}>
                <span className={`label ${c.kind === 'Hardware' ? 'label-warning' : 'label-info'}`}>{c.kind}</span>
              </td>
              <td>{c.message}</td>
              <td className="align-right" style={{ whiteSpace: 'nowrap', color: '#999' }} title={c.date}>
                {fromNow(c.when, now)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {hidden.length > 0 && (
        <a href={href} className="font-11 tk-changes-more" style={{ display: 'inline-block', marginBottom: 10 }} {...NEW_TAB}>
          +{hidden.length} more
        </a>
      )}
    </Section>
  )
}
